import { CSSProperties, ReactElement, useState } from 'react';
import { Box, Divider, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';

import { QueueProcessingSection } from '../models/enums/QueueProcessingSection';

const logoImageStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    transition: 'opacity 0.2s ease',
};

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 60;

type QueueMenuItem = {
    section: QueueProcessingSection;
    label: string;
    icon: ReactElement;
};

const menuItems: QueueMenuItem[] = [
    { section: QueueProcessingSection.processes, label: 'Batches', icon: <AccountTreeOutlinedIcon sx={{ color: '#1976d2' }} /> },
];

interface IProps {
    activeSection: QueueProcessingSection;
    onSectionChange: (section: QueueProcessingSection) => void;
}

export function QueueProcessingSidebar({ activeSection, onSectionChange }: IProps) {
    const [expanded, setExpanded] = useState(true);

    return (
        <Drawer
            variant="permanent"
            sx={{
                width: expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
                    overflowX: 'hidden',
                    transition: 'width 0.2s ease',
                    boxSizing: 'border-box',
                    position: 'relative',
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: expanded ? 'flex-end' : 'center',
                    padding: '4px 8px',
                }}
            >
                <Tooltip title={expanded ? 'Collapse menu' : 'Expand menu'} placement="right">
                    <IconButton onClick={() => setExpanded(!expanded)} size="small">
                        {expanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                    </IconButton>
                </Tooltip>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: expanded ? 'flex-start' : 'center',
                    alignItems: 'center',
                    padding: '6px 8px 10px',
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: expanded ? 215 : 36,
                        height: 36,
                        flexShrink: 0,
                        transition: 'width 0.2s ease',
                    }}
                >
                    <img
                        src="/project_images/katalyst.png"
                        alt="Katalyst"
                        style={{ ...logoImageStyle, opacity: expanded ? 1 : 0, objectPosition: 'left center' }}
                    />
                    <img
                        src="/project_images/katalyst_mini.png"
                        alt="Katalyst"
                        style={{ ...logoImageStyle, opacity: expanded ? 0 : 1, objectPosition: 'center' }}
                    />
                </Box>
            </Box>
            <Divider />
            <List disablePadding>
                {menuItems.map(({ section, label, icon }) => (
                    <Tooltip
                        key={section}
                        title={expanded ? '' : label}
                        placement="right"
                    >
                        <ListItemButton
                            selected={activeSection === section}
                            onClick={() => onSectionChange(section)}
                            sx={{ minHeight: 48, px: 2 }}
                        >
                            <ListItemIcon sx={{ minWidth: expanded ? 40 : 'auto' }}>
                                {icon}
                            </ListItemIcon>
                            {expanded && (
                                <ListItemText primary={label} />
                            )}
                        </ListItemButton>
                    </Tooltip>
                ))}
            </List>
        </Drawer>
    );
}
